import { ReactElement } from 'react'

import type { ItemType } from 'antd/es/menu/interface'
import { Translation, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Icon } from 'components/core'
import { SHARED_WITH_ME } from 'const/dashboards'
import {
    userHasAmazonAdvertisingPermissions,
    userHasDashboardsOnlyPermissions,
    userHasReadOrManageSegmentPermissions,
    userHasSovReadPermissions,
} from 'helpers/featurePermissions'
import { FeatureFlagsSet } from 'hooks/useFeatureFlags'
import { GatedFeatures } from 'hooks/useGatedFeatures'
import { getPath } from 'routing/pagePaths'
import { PageId } from 'routing/pages'
import { dashboardsUrl } from 'routing/urls'
import { UserPermission } from 'types'

type NavTagVariant = 'new' | 'dev'

const NavTag = ({ variant }: { variant: NavTagVariant }): ReactElement => {
    const { t } = useTranslation('common')

    const labels: Record<NavTagVariant, string> = {
        new: t('common:new', 'New'),
        dev: t('common:dev', 'Dev'),
    }

    return (
        <span
            style={{
                fontSize: 9,
                lineHeight: 1,
                padding: '3px 4px',
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: 'inherit',
                opacity: 0.65,
                fontWeight: 500,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                position: 'absolute',
                right: 8,
            }}
        >
            {labels[variant]}
        </span>
    )
}

export type NavItemKey =
    | 'aiAgents'
    | 'aiArtifacts'
    | 'amazonAdAccounts'
    | 'amazonAdProducts'
    | 'amazonCampaigns'
    | 'amazonDsp'
    | 'amazonPortfolio'
    | 'amazonTargeting'
    | 'automation'
    | 'categoryPerformance'
    | 'customDashboards'
    | 'distributionAnalysis'
    | 'homePage'
    | 'keywordLists'
    | 'keywordResearch'
    | 'keywordResearchV2'
    | 'labels'
    | 'marketSegment'
    | 'marketAnalysis'
    | 'marketBrands'
    | 'marketProducts'
    | 'marketSellers'
    | 'productCatalog'
    | 'settings'
    | 'shareOfVoice'
    | 'workflows'

interface NavItem {
    page: PageId
    title: string | ReactElement
    icon: ReactElement
    getPath: (
        featurePermissions: UserPermission[],
        featureFlags: FeatureFlagsSet
    ) => string
    getVisible: (
        featurePermissions: UserPermission[],
        featureFlags: FeatureFlagsSet,
        gatedFeatures: GatedFeatures
    ) => boolean
}

export const NAVIGATION_ITEMS: Record<NavItemKey, NavItem> = {
    amazonAdAccounts: {
        page: 'adAccountsSummaryPage',
        title: (
            <Translation>
                {(t) => t('common:advertisingAccounts', 'Ad Accounts')}
            </Translation>
        ),
        icon: <Icon name="NAV_AD_ACCOUNTS" size={18} />,
        getPath: () => getPath('adAccountsSummaryPage'),
        getVisible: () => true,
    },
    amazonAdProducts: {
        page: 'productsSummaryPage',
        title: (
            <Translation>
                {(t) => t('common:advertisedProducts', 'Advertising Products')}
            </Translation>
        ),
        icon: <Icon name="NAV_ADVERTISING_PRODUCTS" size={18} />,
        getPath: () => getPath('productsSummaryPage'),
        getVisible: () => true,
    },
    amazonCampaigns: {
        page: 'campaignsSummaryPage',
        title: (
            <Translation>
                {(t) => t('common:campaignManager', 'Campaign Manager')}
            </Translation>
        ),
        icon: <Icon name="NAV_CAMPAIGNS" size={18} />,
        getPath: () => getPath('campaignsSummaryPage'),
        getVisible: () => true,
    },
    amazonDsp: {
        page: 'dspSummaryPage',
        title: <Translation>{(t) => t('common:dsp', 'DSP')}</Translation>,
        icon: <Icon name="NAV_DSP" size={18} />,
        getPath: () => getPath('dspSummaryPage'),
        getVisible: () => true,
    },
    amazonPortfolio: {
        page: 'portfoliosSummaryPage',
        title: (
            <Translation>
                {(t) => t('common:portfolios', 'Portfolios')}
            </Translation>
        ),
        icon: <Icon name="NAV_PORTFOLIO" size={18} />,
        getPath: () => getPath('portfoliosSummaryPage'),
        getVisible: () => true,
    },
    amazonTargeting: {
        page: 'targetingSummaryPage',
        title: (
            <Translation>
                {(t) => t('common:targeting', 'Targeting')}
            </Translation>
        ),
        icon: <Icon name="NAV_TARGETING" size={18} />,
        getPath: () => getPath('targetingSummaryPage'),
        getVisible: () => true,
    },
    workflows: {
        page: 'aiPage',
        title: (
            <Translation>
                {(t) => <>{t('common:aiAnalyst', 'Ask Cobalt')}</>}
            </Translation>
        ),
        icon: <Icon name="ASK_COBALT_SPARKLE" size={18} />,
        getPath: () => getPath('aiPage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions),
    },
    // Artifacts/Agents are sub-routes of the Rocket `./Ai` remote (the same
    // one `workflows` mounts), so they reuse the `aiPage` PageId and need no
    // route of their own — same pattern as marketSellers/marketBrands/
    // marketProducts below.
    aiArtifacts: {
        page: 'aiPage',
        title: (
            <Translation>
                {(t) => t('common:aiArtifacts', 'Artifacts')}
            </Translation>
        ),
        icon: <Icon name="NAV_AI_ARTIFACTS" size={18} />,
        getPath: () => `${getPath('aiPage')}/artifacts`,
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions),
    },
    aiAgents: {
        page: 'aiPage',
        title: (
            <>
                <Translation>
                    {(t) => t('common:aiAgents', 'Agents')}
                </Translation>
                <NavTag variant="new" />
            </>
        ),
        icon: <Icon name="NAV_AI_AGENTS" size={18} />,
        getPath: () => `${getPath('aiPage')}/agents`,
        getVisible: (featurePermissions, featureFlags) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            featureFlags.scheduledAgents,
    },
    automation: {
        page: 'automationPage',
        title: (
            <Translation>
                {(t) => t('common:adAutomations', 'Ad Automations')}
            </Translation>
        ),
        icon: <Icon name="NAV_AUTOMATIONS" size={18} />,
        getPath: () => getPath('automationPage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            userHasAmazonAdvertisingPermissions(featurePermissions),
    },
    categoryPerformance: {
        page: 'categoryPerformanceSummaryPage',
        title: (
            <Translation>
                {(t) => t('common:categoryPerformance', 'Category Performance')}
            </Translation>
        ),
        icon: <Icon name="NAV_CATEGORY_PERFORMANCE" size={18} />,
        getPath: () => getPath('categoryPerformanceSummaryPage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions),
    },
    customDashboards: {
        page: 'dashboardsSummaryPage',
        title: (
            <Translation>
                {(t) => t('common:dashboards', 'Custom Dashboards')}
            </Translation>
        ),
        icon: <Icon name="NAV_CUSTOM_DASHBOARDS" size={18} />,
        getPath: (featurePermissions) =>
            userHasDashboardsOnlyPermissions(featurePermissions ?? [])
                ? dashboardsUrl(SHARED_WITH_ME)
                : getPath('dashboardsSummaryPage'),
        getVisible: () => true,
    },
    homePage: {
        page: 'homePage',
        title: (
            <Translation>
                {(t) => t('navigation:SideMenu.home', 'Home')}
            </Translation>
        ),
        icon: <Icon name="NAV_HOME" size={18} />,
        getPath: () => getPath('homePage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions),
    },
    keywordLists: {
        page: 'keywordResearchListsPage',
        title: (
            <Translation>
                {(t) =>
                    t(
                        'navigation:SideMenu.keywordResearchLists',
                        'Keyword Lists'
                    )
                }
            </Translation>
        ),
        icon: <Icon name="NAV_KEYWORD_LISTS" size={18} />,
        getPath: () => getPath('keywordResearchListsPage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            userHasSovReadPermissions(featurePermissions),
    },
    keywordResearch: {
        page: 'keywordResearchPage',
        title: (
            <Translation>
                {(t) =>
                    t('navigation:SideMenu.keywordResearch', 'Keyword Research')
                }
            </Translation>
        ),
        icon: <Icon name="NAV_KEYWORD_RESEARCH" size={18} />,
        getPath: () => getPath('keywordResearchPage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            userHasSovReadPermissions(featurePermissions),
    },
    keywordResearchV2: {
        page: 'keywordResearchV2Page',
        title: (
            <>
                <Translation>
                    {(t) =>
                        t(
                            'navigation:SideMenu.keywordResearchV2',
                            'Research V2'
                        )
                    }
                </Translation>
                <NavTag variant="dev" />
            </>
        ),
        icon: <Icon name="NAV_KEYWORD_RESEARCH" size={18} />,
        getPath: () => getPath('keywordResearchV2Page'),
        getVisible: (featurePermissions, featureFlags) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            userHasSovReadPermissions(featurePermissions) &&
            featureFlags.keywordResearchV2,
    },
    labels: {
        page: 'settingsPage',
        title: <Translation>{(t) => t('common:labels', 'Labels')}</Translation>,
        icon: <Icon name="NAV_LABELS" size={18} />,
        getPath: () => `${getPath('settingsPage')}/labels`,
        getVisible: () => true,
    },
    marketSegment: {
        page: 'segmentsSummaryPage',
        title: (
            <Translation>
                {(t) => t('common:marketSegments', 'Market Segments')}
            </Translation>
        ),
        icon: <Icon name="NAV_MARKET_SEGMENTS" size={18} />,
        getPath: () => getPath('segmentsSummaryPage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            userHasReadOrManageSegmentPermissions(featurePermissions),
    },
    marketAnalysis: {
        page: 'marketAnalysis',
        title: (
            <Translation>
                {(t) => t('common:marketAnalysis', 'Market Analysis')}
            </Translation>
        ),
        icon: <Icon name="NAV_MARKET_ANALYSIS" size={18} />,
        getPath: () => getPath('marketAnalysis'),
        getVisible: (featurePermissions, _featureFlags, gatedFeatures) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            gatedFeatures.marketAnalysis,
    },
    // Sellers/Brands/Products are sub-routes of the Rocket `./MarketAnalysis`
    // remote, so they reuse the `marketAnalysis` PageId and need no route of
    // their own. The longest-prefix rule in SideMenuMainNavigation gives them
    // the selected state over Market Analysis itself.
    marketSellers: {
        page: 'marketAnalysis',
        title: (
            <Translation>{(t) => t('common:sellers', 'Sellers')}</Translation>
        ),
        icon: <Icon name="NAV_AD_ACCOUNTS" size={18} />,
        getPath: () => `${getPath('marketAnalysis')}/sellers`,
        getVisible: (featurePermissions, featureFlags, gatedFeatures) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            gatedFeatures.marketAnalysis &&
            featureFlags.marketTrendsStandalonePages,
    },
    marketBrands: {
        page: 'marketAnalysis',
        title: <Translation>{(t) => t('common:brands', 'Brands')}</Translation>,
        icon: <Icon name="NAV_PRODUCT_CATALOG" size={18} />,
        getPath: () => `${getPath('marketAnalysis')}/brands`,
        getVisible: (featurePermissions, featureFlags, gatedFeatures) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            gatedFeatures.marketAnalysis &&
            featureFlags.marketTrendsStandalonePages,
    },
    marketProducts: {
        page: 'marketAnalysis',
        title: (
            <Translation>{(t) => t('common:products', 'Products')}</Translation>
        ),
        icon: <Icon name="NAV_ADVERTISING_PRODUCTS" size={18} />,
        getPath: () => `${getPath('marketAnalysis')}/products`,
        getVisible: (featurePermissions, featureFlags, gatedFeatures) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            gatedFeatures.marketAnalysis &&
            featureFlags.marketTrendsStandalonePages,
    },
    productCatalog: {
        page: 'productCatalogPage',
        title: (
            <Translation>
                {(t) =>
                    t('navigation:productCatalog.pageTitle', 'Product Catalog')
                }
            </Translation>
        ),
        icon: <Icon name="NAV_PRODUCT_CATALOG" size={18} />,
        getPath: () => getPath('productCatalogPage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions ?? []),
    },
    distributionAnalysis: {
        page: 'distributionAnalysisPage',
        title: (
            <Translation>
                {(t) =>
                    t(
                        'navigation:sellerCompliance.pageTitle',
                        'Seller Compliance'
                    )
                }
            </Translation>
        ),
        icon: <Icon name="NAV_SELLER_COMPLIANCE" size={18} />,
        getPath: () => getPath('distributionAnalysisPage'),
        getVisible: (featurePermissions, _featureFlags, gatedFeatures) =>
            !userHasDashboardsOnlyPermissions(featurePermissions ?? []) &&
            gatedFeatures.sellerCompliance,
    },
    settings: {
        page: 'settingsPage',
        title: (
            <Translation>{(t) => t('common:settings', 'Settings')}</Translation>
        ),
        icon: <Icon name="NAV_SETTINGS" size={18} />,
        getPath: () => getPath('settingsPage'),
        getVisible: () => true,
    },
    shareOfVoice: {
        page: 'sovKeywordsSummaryPage',
        title: (
            <Translation>
                {(t) =>
                    t('navigation:SideMenu.shelfIntelligence', 'Share of Voice')
                }
            </Translation>
        ),
        icon: <Icon name="NAV_SHARE_OF_VOICE" size={18} />,
        getPath: () => getPath('sovKeywordsSummaryPage'),
        getVisible: (featurePermissions) =>
            !userHasDashboardsOnlyPermissions(featurePermissions) &&
            userHasSovReadPermissions(featurePermissions),
    },
}

type PageItemType = ItemType & { page: PageId; path: string }

export const getVisibleNavItems = (
    featurePermissions: UserPermission[],
    featureFlags: FeatureFlagsSet,
    gatedFeatures: GatedFeatures
): Record<NavItemKey, PageItemType> => {
    return Object.fromEntries(
        Object.entries(NAVIGATION_ITEMS)
            .filter(([_, item]) =>
                item.getVisible(featurePermissions, featureFlags, gatedFeatures)
            )
            .map(([itemKey, item]) => {
                const path = item.getPath(featurePermissions, featureFlags)
                return [
                    itemKey as NavItemKey,
                    {
                        key: itemKey,
                        page: item.page,
                        path: path,
                        icon: item.icon,
                        label: <Link to={path}>{item.title}</Link>,
                    },
                ]
            })
    ) as Record<NavItemKey, PageItemType>
}
